<script setup lang="ts">
  import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { Table } from '../models/Table'
  import { TableSchema } from '../models/TableSchema'
  import { TableFieldType } from '../models/TableFieldType'
  import ArrayTableBuilder from './ArrayTableBuilder.vue'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { TablesService } from '../services/TablesService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import {
    AppstoreOutlined,
    BorderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import ArrayValuesTableConfiguration from './ArrayValuesTableConfiguration.vue'
  import JsonTableConfiguration from './JsonTableConfiguration.vue'
  import { TablesJsonTableConfigurationService } from '../services/TablesJsonTableConfigurationService'
  import SelectOrCreateTableConfiguration from './SelectOrCreateTableConfiguration.vue'
  import ObjectTableBuilder from './ObjectTableBuilder.vue'
  import { TableSchemaUiFieldGroup } from '../models/TableSchemaUi'
  import { TableField } from '../models/TableField'
  import ColorTableConfiguration from './ColorTableConfiguration.vue'
  import ColumnMultiSelectOrCreateTableConfiguration from './ColumnMultiSelectOrCreateTableConfiguration.vue'
  import TableHelpPopover from '../help/components/TableHelpPopover.vue'
  import RegexTablesConfiguration from './RegexTablesConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { TableDataSourceType } from '../models/TableDataSourceType'
  import TableDataSourceSelectConfiguration from './TableDataSourceSelectConfiguration.vue'
  import TableColumnTypeConfiguration from './TableColumnTypeConfiguration.vue'
  import { TablesUiService } from '../services/TablesUiService'
  import { AnalysisWizardModel } from '@/components/AnalysisWizard/models/AnalysisWizardModel'

  interface ITableConfigurationProps {
    tableSchemas: Array<TableSchema>
    table: Table | null
    rootTable: Table | null
    currentPath?: string[]
    uiFieldGroup?: TableSchemaUiFieldGroup
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      value: string
      label: string
      id: string
      type: TableDataSourceType
    }>
    selectedDataSource?: { value: string; label: string; id: string; type: TableDataSourceType }
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    wizardModel?: AnalysisWizardModel
  }

  interface ITableConfigurationEmits {
    (
      e: 'configuration-updated',
      table: Table,
      config: Record<string, any>,
      tableTypeChange?: boolean
    ): void
    (
      e: 'data-source-selected',
      value: { value: string; label: string; id: string; type: TableDataSourceType }
    ): void
    (e: 'generating-rows', value: boolean): void
    (e: 'row-generation-complete', success: boolean): void
  }

  const props = defineProps<ITableConfigurationProps>()
  const emit = defineEmits<ITableConfigurationEmits>()

  const formRef = ref()
  const configuration = ref<Record<string, any>>({})

  const isCollapsed = ref<boolean>(false)
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  const arrayBuilderRef = ref<any>(null)

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const tableDescription = ref(props.table?.configuration?.description ?? '')

  watch(tableDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{
    field: string
    value: string
    disableSmartParse: boolean
  }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value, disableSmartParse }) => ({ field, value, disableSmartParse }))
        )
      )
    )
    .subscribe(({ field, value, disableSmartParse }) => {
      if (!disableSmartParse) {
        const parsedValue = parseSmartValue(value)
        configuration.value[field] = parsedValue
      } else {
        configuration.value[field] = value
      }
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.table) return
    configuration.value = TablesService.initializeTableConfiguration(
      props.table,
      props.table.schema.fields
    )
  }

  const handleDataSourceSelected = (value: {
    value: string
    label: string
    id: string
    type: TableDataSourceType
  }) => {
    emit('data-source-selected', value)
  }

  const handleConfigurationChange = () => {
    if (props.table) {
      emit('configuration-updated', props.table, configuration.value)
    }
  }

  const handleGeneratingRowsChange = async (value: boolean) => {
    emit('generating-rows', value)
  }

  const handleGeneratingRowsCompleted = async (success: boolean) => {
    emit('row-generation-complete', success)

    if (success === true) {
      const refVal = arrayBuilderRef.value
      if (!refVal) return
      await nextTick()
      if (Array.isArray(refVal)) {
        refVal.forEach((r: any) => r?.refresh?.())
      } else {
        refVal.refresh?.()
      }
    }
  }

  const handleChildTableTypeChanged = (
    tableType: string,
    childTable: Table,
    configuration: any
  ) => {
    if (childTable) {
      emit('configuration-updated', { ...childTable, _type: tableType }, configuration, true)
    }
  }

  const handleTableTypeChanged = (tableType: string, configuration: any) => {
    if (props.table) {
      emit(
        'configuration-updated',
        { ...props.table, _type: tableType },
        configuration?.value,
        true
      )
    }
  }

  watch(
    () => props.table,
    (newTable) => {
      if (newTable) {
        configuration.value = { ...newTable.configuration }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
        tableDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string, disableSmartParse?: boolean) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({
      value: newValue,
      field: fieldName,
      disableSmartParse: disableSmartParse ?? false
    })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props.table
      ? props.table.schema.fields.filter(
          (f: any) =>
            f.field_type !== TableFieldType.Object &&
            f.field_type !== TableFieldType.Array &&
            f.field_type !== TableFieldType.Json
        ).length
      : 0
  )

  const configuredFields = computed(() => {
    const table = props.table
    const uiFieldGroup = props.uiFieldGroup

    if (!table) {
      return []
    }

    const allFields = table.schema.fields

    if (!uiFieldGroup || !Array.isArray(uiFieldGroup.fields) || uiFieldGroup.fields.length === 0) {
      return allFields
    }

    // Ensure order matches uiFieldGroup.fields
    return uiFieldGroup.fields
      .map((fieldName) => allFields.find((field: TableField) => field.name === fieldName))
      .filter((field): field is TableField => !!field)
  })

  const levelBasedBackgroundColor = computed(() => {
    // backgroundColorClamp configurationLevel to min 1
    const level = Math.max(props.configurationLevel, 1)
    // Opacity increases by 1% per level, starting from 0% at level 1
    const opacity = Math.min((level - 1) * 0.001, 1)
    return `rgba(0, 0, 0, ${opacity.toFixed(2)})`
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.table?.schema?.fields?.findIndex(
        (x: TableField) =>
          x.field_type === TableFieldType.Array ||
          x.field_type === TableFieldType.Object ||
          x.field_type === TableFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: TableField): boolean => {
    return field.field_type !== TableFieldType.Object && field.field_type !== TableFieldType.Json
  }

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.table?.display_field as string | Array<string> | null | undefined
    if (mappedField == null) return undefined

    const fields = props.table?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div
    v-if="table"
    :style="{ backgroundColor: levelBasedBackgroundColor }"
    :class="[
      'border',
      'table-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-table-configuration': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <!-- Only show radio group if configurationLevel is 1 or less -->
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mt-1 text-lg font-semibold">
              Configure: {{ table.display_name }}
              <span class="ml-1" v-if="displayField != null">{{ `(${displayField})` }}</span>
            </h3>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          table._type !== TablesService.CUSTOM_TABLE_TYPE && props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Description</label>
        <Input
          v-model:value="tableDescription"
          @change="handleConfigurationChange"
          placeholder="Table description"
          :size="controlSize"
        />
      </div>

      <!-- Configuration Form -->
      <div :class="['flex-1', 'overflow-y-auto', { 'collapsed-table-configuration': isCollapsed }]">
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'table-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredFields"
              :key="configuredField.name"
              class="table-form-field-grid"
              :class="{
                'array-table-builder-grid-span':
                  configuredField.field_type === TableFieldType.Array,
                'object-table-builder-grid-span':
                  configuredField.field_type === TableFieldType.Object ||
                  configuredField.field_type === TableFieldType.StringTemplate,
                'json-table-configuration-grid-span':
                  configuredField.field_type === TableFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field_type !== TableFieldType.Object &&
                  configuredField.field_type !== TableFieldType.Array &&
                  configuredField.field_type !== TableFieldType.Json,
                '!hidden': !TablesUiService.isFieldVisibleFromRoot(
                  configuredField,
                  props.rootTable,
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                )
              }"
            >
              <div class="flex">
                <div v-if="shouldShowFieldInformation(configuredField)" class="min-w-0 flex-1">
                  <label class="table-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.display_name }}
                    <span v-if="configuredField.required" class="text-red-500">*</span>
                  </label>
                  <p v-if="configuredField.description" class="table-field-label mb-2 text-xs">
                    {{ configuredField.description }}
                  </p>
                </div>

                <div
                  v-if="
                    configuredField.help_content != null && configuredField.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <TableHelpPopover
                    :content-type="configuredField.help_content"
                    :title="`${configuredField.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->

              <Input
                v-if="configuredField.field_type === TableFieldType.Text"
                :value="configuration[configuredField.name]"
                @update:value="
                  (val: string) =>
                    onInputChange(val, configuredField.name, configuredField.disable_smart_parse)
                "
                @blur="() => onInputBlur(configuredField.name)"
                :data-field="`${props.configurationLevel}:${configuredField.name}`"
                :readonly="configuredField.readonly"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field_type === TableFieldType.Number"
                v-model:value="configuration[configuredField.name]"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
                @change="handleConfigurationChange"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
              />

              <!-- Regex Input -->
              <RegexTablesConfiguration
                v-else-if="configuredField.field_type === TableFieldType.RegularExpression"
                v-model="configuration[configuredField.name]"
                :required="configuredField.required"
                :regex="configuredField.field_type_args?.regex_pattern"
                :data-type="configuredField.field_type_args?.regex_data_type"
                :placeholder="configuredField.placeholder || ''"
                :error-message="configuredField.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field_type === TableFieldType.Select"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field_type === TableFieldType.SelectJson"
                :value="JSON.stringify(configuration[configuredField.name])"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      configuration[configuredField.name] = parsed
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreateTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.SelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()}`"
                :options="configuredField.options"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field_type === TableFieldType.MultiSelect ||
                  configuredField.field_type === TableFieldType.ColumnMultiSelect ||
                  configuredField.field_type === TableFieldType.ParamMultiSelect ||
                  configuredField.field_type === TableFieldType.ParamOrColumnMultiSelect
                "
                v-model:value="configuration[configuredField.name]"
                mode="multiple"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field_type === TableFieldType.MultiSelect"
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === TableFieldType.ColumnMultiSelect"
                  v-for="option in props.availableColumns"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === TableFieldType.ParamMultiSelect"
                  v-for="option in props.availableParams"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === TableFieldType.ParamOrColumnMultiSelect"
                  v-for="option in [...props.availableColumns, ...props.availableParams]"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field_type === TableFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="configuration[configuredField.name]"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.display_name }}
              </Checkbox>

              <!-- Column Select -->
              <Select
                v-else-if="configuredField.field_type === TableFieldType.ColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableColumns"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Param Select -->
              <Select
                v-else-if="configuredField.field_type === TableFieldType.ParamSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableParams"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Param or Column Select -->
              <Select
                v-else-if="configuredField.field_type === TableFieldType.ParamOrColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :size="controlSize"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in [...props.availableParams, ...props.availableColumns]"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Column Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === TableFieldType.ColumnSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableColumns"
                :placeholder="
                  configuredField.placeholder || `Select existing column or enter new column name`
                "
                :size="controlSize"
                class="w-full"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Param Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === TableFieldType.ParamSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableParams"
                :placeholder="
                  configuredField.placeholder || `Select existing param or enter new param`
                "
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Param or Column Select or Create -->
              <AutoComplete
                v-else-if="
                  configuredField.field_type === TableFieldType.ParamOrColumnSelectOrCreate
                "
                v-model:value="configuration[configuredField.name]"
                :options="[...props.availableColumns, ...props.availableParams]"
                :placeholder="
                  configuredField.placeholder ||
                  `Select existing param or column or enter new value`
                "
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Multi Column Select Or Create -->
              <ColumnMultiSelectOrCreateTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.ColumnMultiSelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableColumns"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Param Column Multi Select Or Create -->
              <ColumnMultiSelectOrCreateTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.ParamMultiSelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableParams"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Param or Column Column Multi Select Or Create -->
              <ColumnMultiSelectOrCreateTableConfiguration
                v-else-if="
                  configuredField.field_type === TableFieldType.ParamOrColumnMultiSelectOrCreate
                "
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="[...props.availableColumns, ...props.availableParams]"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Values -->
              <ArrayValuesTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.ArrayValues"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Picker -->
              <ColorTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.ColorPicker"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select DataSource -->
              <TableDataSourceSelectConfiguration
                v-else-if="configuredField.field_type === TableFieldType.SelectDataSource"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()} value`"
                :available-data-sources="props.availableDataSources"
                :data-source-types="configuredField.field_type_args.datasource_types"
                @update:modelValue="handleConfigurationChange"
                @data-source-selected="handleDataSourceSelected"
              />

              <!-- Array Type -->
              <ArrayTableBuilder
                ref="arrayBuilderRef"
                v-else-if="configuredField.field_type === TableFieldType.Array"
                :root-table="props.rootTable"
                :configuration-level="props.configurationLevel"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                :available-data-sources="props.availableDataSources"
                v-model="configuration[configuredField.name]"
                :table-field="configuredField"
                :key="table.id"
                @update:modelValue="handleConfigurationChange"
                @child-table-type-changed="handleChildTableTypeChanged"
                @data-source-selected="handleDataSourceSelected"
                @generating-rows="handleGeneratingRowsChange"
                @row-generation-complete="handleGeneratingRowsCompleted"
                :selected-data-source="selectedDataSource"
                :table-schemas="tableSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
                :wizard-model="wizardModel"
              />

              <!-- Object Type -->
              <ObjectTableBuilder
                v-else-if="configuredField.field_type === TableFieldType.Object"
                :configuration-level="props.configurationLevel + 1"
                :root-table="props.rootTable"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                :available-data-sources="props.availableDataSources"
                v-model="configuration[configuredField.name]"
                :table-field="configuredField"
                @update:modelValue="handleConfigurationChange"
                @data-source-selected="handleDataSourceSelected"
                :selected-data-source="selectedDataSource"
                :table-schemas="tableSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
                :wizard-model="wizardModel"
              />

              <!-- String Template -->
              <StringTemplateBuilder
                v-else-if="configuredField.field_type === TableFieldType.StringTemplate"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :delimiter_start="configuredField.field_type_args?.delimiter_start"
                :delimiter_end="configuredField.field_type_args?.delimiter_end"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Table Column Type Select -->
              <TableColumnTypeConfiguration
                v-else-if="configuredField.field_type === TableFieldType.TableColumnTypeSelect"
                :table-schemas="tableSchemas"
                v-model="configuration[configuredField.name]"
                :configuration="configuration"
                :configured-field="configuredField"
                :selected-data-source="selectedDataSource"
                :available-data-sources="availableDataSources"
                :configuration-level="props.configurationLevel + 1"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                @generating-rows="handleGeneratingRowsChange"
                @row-generation-complete="handleGeneratingRowsCompleted"
                @configuration-updated="
                  (cfg) => {
                    if (!cfg) return
                    configuration = { ...(cfg || {}) }
                    handleConfigurationChange()
                  }
                "
                :global-collapsed="globalCollapsed"
                :wizard-model="wizardModel"
              />

              <!-- Json Type (Custom Rule) -->
              <JsonTableConfiguration
                v-else-if="configuredField.field_type === TableFieldType.Json"
                :table-schemas="props.tableSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration"
                :editor-schema="TablesJsonTableConfigurationService.getJsonEditorSchemaForTable()"
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @table-type-changed="handleTableTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonTableConfiguration
                v-else
                :table-schemas="props.tableSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :editor-schema="
                  TablesJsonTableConfigurationService.getJsonEditorSchemaForField(configuredField)
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Table Selected</p>
      <p class="text-sm">Select a table from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .table-configuration {
    padding: 10px;
  }

  .collapsed-table-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .table-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .table-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .table-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-table-builder-grid-span,
  .object-table-builder-grid-span,
  .json-table-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .table-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .table-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }
  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }
  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
